import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CheckCircle2, ChevronDown, ChevronRight, Clock, ExternalLink, Flag, LoaderCircle, TimerReset, Users } from 'lucide-react'
import { format } from 'date-fns'
import { workloadApi } from '../api/workload'
import { Avatar } from '../components/common/Avatar'
import { formatRoleLabel } from '../lib/roles'
import { formatWorkType } from '../lib/workTypes'
import type { DashboardAssignedTask, DashboardDay, DashboardLog, DashboardMember } from '../types'

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const parseDateKey = (date: string) => {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const formatHours = (hours: number) => {
  if (!Number.isFinite(hours) || hours <= 0) return '0h'
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  if (minutes === 60) return `${wholeHours + 1}h`
  return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`
}

const formatVariance = (actual: number, estimated: number) => {
  if (estimated <= 0) return '-'
  const diff = actual - estimated
  if (Math.abs(diff) < 0.01) return '0h'
  return `${diff > 0 ? '+' : '-'}${formatHours(Math.abs(diff))}`
}

const capacityColor = (actual: number, target: number) => {
  if (actual === 0) return '#94a3b8'
  if (actual > target) return '#ef4444'
  if (actual >= target * 0.85) return '#16a34a'
  if (actual >= target * 0.45) return '#2563eb'
  return '#f59e0b'
}

const getCurrentLabel = (
  labels?: Array<{
    label: {
      id: string
      name: string
      color: string
    }
  }>
) => labels?.[0]?.label || null

export default function WorkloadPage() {
  const navigate = useNavigate()
  const today = todayDateInput()
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [employeeId, setEmployeeId] = useState('all')

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-review', startDate, endDate, employeeId],
    queryFn: () => workloadApi.getDashboard({ startDate, endDate, employeeId }),
  })

  const review = data?.data?.data
  const selectedDays = review?.daily || []
  const isRange = startDate !== endDate
  const totalTarget = (review?.targetHoursPerMember || 8) * (review?.employeeCount || 0) * Math.max(selectedDays.length, 1)
  const totalDifference = (review?.totalHours || 0) - (review?.estimatedHours || 0)

  const expandedDefaults = useMemo(() => {
    const next = new Set<string>()
    selectedDays.forEach((day) => {
      day.members.forEach((member) => {
        if (member.logs.length > 0) next.add(`${day.date}:${member.user.id}`)
      })
    })
    return next
  }, [selectedDays])

  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>({})
  const isExpanded = (key: string) => manualExpanded[key] ?? expandedDefaults.has(key)
  const toggleExpanded = (key: string) => setManualExpanded((current) => ({ ...current, [key]: !isExpanded(key) }))

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>Team accountability</div>
          <h1 style={titleStyle}>Dashboard</h1>
          <p style={subtitleStyle}>
            Daily review of actual time logged against estimated work. Open any task to inspect comments, history, and attachments.
          </p>
        </div>

        <div style={filterBarStyle}>
          {review?.canSelectEmployee && (
            <label style={dateLabelStyle}>
              Employee
              <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)} style={employeeSelectStyle}>
                <option value="all">All employees</option>
                {review.employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.fullName}</option>
                ))}
              </select>
            </label>
          )}
          <label style={dateLabelStyle}>
            Start
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={dateInputStyle} />
          </label>
          <label style={dateLabelStyle}>
            End
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={dateInputStyle} />
          </label>
          <button type="button" onClick={() => { setStartDate(today); setEndDate(today) }} style={todayButtonStyle}>
            Today
          </button>
        </div>
      </div>

      <div style={statsGridStyle}>
        <SummaryStat label="Actual Time" value={formatHours(review?.totalHours || 0)} icon={<Clock size={18} />} tone="#2563eb" />
        <SummaryStat label="Estimated Time" value={formatHours(review?.estimatedHours || 0)} icon={<TimerReset size={18} />} tone="#7c3aed" />
        <SummaryStat
          label="Difference"
          value={review?.estimatedHours ? `${totalDifference >= 0 ? '+' : '-'}${formatHours(Math.abs(totalDifference))}` : '-'}
          icon={<CalendarDays size={18} />}
          tone={totalDifference > 0 ? '#ef4444' : '#16a34a'}
        />
        <SummaryStat label="Employees" value={String(review?.employeeCount || 0)} icon={<Users size={18} />} tone="#0891b2" />
        <SummaryStat label="Tasks" value={String(review?.taskCount || 0)} icon={<ExternalLink size={18} />} tone="#ea580c" />
        <SummaryStat label="Completed" value={String(review?.completedTaskCount || 0)} icon={<CheckCircle2 size={18} />} tone="#16a34a" />
        <SummaryStat label="Progressing" value={String(review?.progressingTaskCount || 0)} icon={<LoaderCircle size={18} />} tone="#2563eb" />
      </div>

      <div style={capacityStripStyle}>
        <div style={{ minWidth: 165 }}>
          <div style={stripLabelStyle}>Selected Capacity</div>
          <div style={stripValueStyle}>{formatHours(review?.totalHours || 0)} / {formatHours(totalTarget)}</div>
        </div>
        <div style={stripTrackStyle}>
          <div
            style={{
              ...stripFillStyle,
              width: `${Math.min(((review?.totalHours || 0) / Math.max(totalTarget, 1)) * 100, 100)}%`,
              background: capacityColor(review?.totalHours || 0, totalTarget),
            }}
          />
        </div>
      </div>

      <AssignedTasksPanel
        tasks={review?.assignedTasks || []}
        canSelectEmployee={review?.canSelectEmployee || false}
        onOpenIssue={(issueId) => navigate(`/issues/${issueId}`)}
      />

      {isLoading ? (
        <div style={emptyStyle}>Loading dashboard...</div>
      ) : selectedDays.length === 0 ? (
        <div style={emptyStyle}>No dashboard data found.</div>
      ) : (
        <div style={dayStackStyle}>
          {selectedDays.map((day) => (
            <DashboardDaySection
              key={day.date}
              day={day}
              isRange={isRange}
              isExpanded={isExpanded}
              onToggle={toggleExpanded}
              onOpenIssue={(issueId) => navigate(`/issues/${issueId}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SummaryStat({ label, value, icon, tone }: { label: string; value: string; icon: React.ReactNode; tone: string }) {
  return (
    <div style={summaryCardStyle}>
      <div style={{ ...summaryIconStyle, color: tone, background: `${tone}14` }}>{icon}</div>
      <div>
        <div style={summaryLabelStyle}>{label}</div>
        <div style={{ ...summaryValueStyle, color: tone }}>{value}</div>
      </div>
    </div>
  )
}

function AssignedTasksPanel({
  tasks,
  canSelectEmployee,
  onOpenIssue,
}: {
  tasks: DashboardAssignedTask[]
  canSelectEmployee: boolean
  onOpenIssue: (issueId: string) => void
}) {
  return (
    <section style={assignedPanelStyle}>
      <div style={assignedHeaderStyle}>
        <div>
          <h2 style={assignedTitleStyle}>Recently Assigned Tasks</h2>
          <div style={assignedSubtitleStyle}>
            Last 20 assigned tasks {canSelectEmployee ? 'for the selected employee view' : 'assigned to you'}.
          </div>
        </div>
        <div style={assignedCountStyle}>{tasks.length}</div>
      </div>

      {tasks.length === 0 ? (
        <div style={assignedEmptyStyle}>No assigned tasks found.</div>
      ) : (
        <div style={assignedListStyle}>
          {tasks.map((task) => (
            <button key={task.id} type="button" onClick={() => onOpenIssue(task.issue.id)} style={assignedRowStyle}>
              <div style={assignedMainWrapStyle}>
                <div style={assignedMainStyle}>
                  <span style={projectDotStyle(task.issue.project?.color || '#64748b')} />
                  <span style={issueKeyStyle}>{task.issue.identifier}</span>
                  <span style={assignedTaskTitleStyle}>{task.issue.title}</span>
                </div>
                <TaskMetaRow
                  stage={task.issue.status.name}
                  label={getCurrentLabel(task.issue.labels)}
                />
              </div>
              {canSelectEmployee && (
                <div style={assignedPersonStyle}>
                  <Avatar user={task.user} size="sm" />
                  <span>{task.user.fullName}</span>
                </div>
              )}
              <StatusBadge label={task.issue.status.name} category={task.issue.status.category} />
              <PriorityText priority={task.issue.priority} />
              <div style={assignedMetricStyle}>Est {task.estimatedHours > 0 ? formatHours(task.estimatedHours) : '-'}</div>
              <ExternalLink size={14} style={{ color: '#94a3b8' }} />
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

function StatusBadge({ label, category }: { label: string; category: string }) {
  const color = category === 'DONE' ? '#16a34a' : category === 'IN_PROGRESS' ? '#2563eb' : '#64748b'
  return (
    <span style={{ ...statusBadgeStyle, color, background: `${color}14` }}>
      {label}
    </span>
  )
}

function TaskMetaRow({
  stage,
  label,
}: {
  stage?: string
  label?: { id: string; name: string; color: string } | null
}) {
  return (
    <div style={taskMetaRowStyle}>
      <span style={taskStageMetaStyle}>{stage || 'No stage'}</span>
      <span style={{ ...taskLabelMetaStyle, color: label?.color || '#64748b', background: `${label?.color || '#64748b'}14` }}>
        {label?.name || 'No label'}
      </span>
    </div>
  )
}

function PriorityText({ priority }: { priority: string }) {
  const color =
    priority === 'URGENT' ? '#dc2626' :
    priority === 'HIGH' ? '#ea580c' :
    priority === 'MEDIUM' ? '#ca8a04' :
    priority === 'LOW' ? '#16a34a' :
    '#64748b'

  return (
    <span style={{ ...priorityTextStyle, color }}>
      <Flag size={13} />
      {priority.replace(/_/g, ' ').toLowerCase()}
    </span>
  )
}

function DashboardDaySection({
  day,
  isRange,
  isExpanded,
  onToggle,
  onOpenIssue,
}: {
  day: DashboardDay
  isRange: boolean
  isExpanded: (key: string) => boolean
  onToggle: (key: string) => void
  onOpenIssue: (issueId: string) => void
}) {
  return (
    <section style={daySectionStyle}>
      <div style={dayHeaderStyle}>
        <div>
          <h2 style={dayTitleStyle}>{isRange ? format(parseDateKey(day.date), 'EEEE, MMM d, yyyy') : 'Today'}</h2>
          <div style={dayMetaStyle}>
            {format(parseDateKey(day.date), 'EEEE, MMM d, yyyy')} · {day.members.length} employees · {day.taskCount} tasks
          </div>
          <div style={dayMetaStyle}>
            {day.completedTaskCount} completed - {day.progressingTaskCount} progressing
          </div>
        </div>
        <div style={dayTotalsStyle}>
          <span>{formatHours(day.totalHours)}</span>
          <span style={{ color: '#94a3b8' }}>/</span>
          <span>{formatHours(day.members.reduce((sum, member) => sum + member.targetHours, 0))}</span>
        </div>
      </div>

      <div style={memberListStyle}>
        {day.members.map((member) => {
          const key = `${day.date}:${member.user.id}`
          return (
            <MemberReview
              key={member.user.id}
              member={member}
              expanded={isExpanded(key)}
              onToggle={() => onToggle(key)}
              onOpenIssue={onOpenIssue}
            />
          )
        })}
      </div>
    </section>
  )
}

function MemberReview({
  member,
  expanded,
  onToggle,
  onOpenIssue,
}: {
  member: DashboardMember
  expanded: boolean
  onToggle: () => void
  onOpenIssue: (issueId: string) => void
}) {
  const pct = Math.min((member.actualHours / Math.max(member.targetHours, 1)) * 100, 100)
  const color = capacityColor(member.actualHours, member.targetHours)

  return (
    <div style={memberBlockStyle}>
      <button type="button" onClick={onToggle} style={memberHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Avatar user={member.user} size="sm" />
          <div style={{ minWidth: 0 }}>
            <div style={memberNameStyle}>{member.user.fullName}</div>
            <div style={memberRoleStyle}>{formatRoleLabel(member.user.role)}</div>
          </div>
        </div>

        <div style={memberMetricsStyle}>
          <div style={capacityBadgeStyle}>
            <span style={{ color }}>{formatHours(member.actualHours)}</span>
            <span style={{ color: '#94a3b8' }}>/</span>
            <span>{formatHours(member.targetHours)}</span>
          </div>
          <div style={miniMetricStyle}>{member.taskCount} tasks</div>
          <div style={{ ...miniMetricStyle, color: '#16a34a' }}>{member.completedTaskCount} done</div>
          <div style={{ ...miniMetricStyle, color: '#2563eb' }}>{member.progressingTaskCount} active</div>
          <div style={miniMetricStyle}>Est {formatHours(member.estimatedHours)}</div>
          <div style={{ ...miniMetricStyle, color: member.actualHours > member.estimatedHours && member.estimatedHours > 0 ? '#ef4444' : '#16a34a' }}>
            {formatVariance(member.actualHours, member.estimatedHours)}
          </div>
        </div>
      </button>

      <div style={memberProgressStyle}>
        <div style={{ ...memberProgressFillStyle, width: `${pct}%`, background: color }} />
      </div>

      {expanded && (
        <div style={taskListStyle}>
          {member.logs.length === 0 ? (
            <div style={noLogsStyle}>No time logged for this day.</div>
          ) : (
            member.logs.map((log) => (
              <TaskLogRow key={log.id} log={log} onOpenIssue={onOpenIssue} />
            ))
          )}
        </div>
      )}
    </div>
  )
}

function TaskLogRow({ log, onOpenIssue }: { log: DashboardLog; onOpenIssue: (issueId: string) => void }) {
  const variance = log.hours - log.estimatedHours
  const hasEstimate = log.estimatedHours > 0

  return (
    <button type="button" onClick={() => onOpenIssue(log.issue.id)} style={taskRowStyle}>
      <div style={taskMainWrapStyle}>
        <div style={taskMainStyle}>
          <span style={projectDotStyle(log.issue.project?.color || '#64748b')} />
          <span style={issueKeyStyle}>{log.issue.identifier}</span>
          <span style={taskTitleStyle}>{log.issue.title}</span>
        </div>
        <TaskMetaRow
          stage={log.issue.status?.name}
          label={getCurrentLabel(log.issue.labels)}
        />
      </div>
      <div style={taskMetricStyle}>Est {hasEstimate ? formatHours(log.estimatedHours) : '-'}</div>
      <div style={taskMetricStyle}>Actual {formatHours(log.hours)}</div>
      <div style={{ ...taskMetricStyle, color: hasEstimate && variance > 0 ? '#ef4444' : '#16a34a' }}>
        {hasEstimate ? formatVariance(log.hours, log.estimatedHours) : '-'}
      </div>
      <LogStatusBadge status={log.status} />
      <ExternalLink size={14} style={{ color: '#94a3b8' }} />
      <div style={taskTypeStyle}>{formatWorkType(log.workType)}</div>
      {log.notes && <div style={taskNotesStyle}>{log.notes}</div>}
    </button>
  )
}

function LogStatusBadge({ status }: { status: DashboardLog['status'] }) {
  const isCompleted = status === 'COMPLETED'
  const color = isCompleted ? '#16a34a' : '#2563eb'
  return (
    <span style={{ ...logStatusBadgeStyle, color, background: `${color}14` }}>
      {isCompleted ? 'Completed' : 'Progressing'}
    </span>
  )
}

const pageStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '100%',
  boxSizing: 'border-box',
  padding: '24px',
  background: '#f7f8fb',
}

const heroStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 20,
  marginBottom: 18,
}

const eyebrowStyle: React.CSSProperties = {
  color: '#2563eb',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
}

const titleStyle: React.CSSProperties = {
  margin: '4px 0 6px',
  fontSize: 28,
  lineHeight: 1.15,
  color: '#111827',
}

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  color: '#64748b',
  fontSize: 14,
  maxWidth: 760,
  lineHeight: 1.5,
}

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 10,
  padding: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
}

const dateLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
}

const dateInputStyle: React.CSSProperties = {
  height: 34,
  border: '1px solid #dbe3ee',
  borderRadius: 6,
  padding: '0 10px',
  fontSize: 13,
  color: '#111827',
}

const employeeSelectStyle: React.CSSProperties = {
  ...dateInputStyle,
  minWidth: 190,
  background: '#ffffff',
}

const todayButtonStyle: React.CSSProperties = {
  height: 34,
  border: '1px solid #bfdbfe',
  borderRadius: 6,
  background: '#eff6ff',
  color: '#1d4ed8',
  fontWeight: 700,
  cursor: 'pointer',
  padding: '0 13px',
}

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 12,
  marginBottom: 14,
}

const summaryCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 74,
  padding: '14px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
}

const summaryIconStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  textTransform: 'uppercase',
  fontWeight: 700,
}

const summaryValueStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 20,
  fontWeight: 800,
}

const capacityStripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  padding: '14px 16px',
  border: '1px solid #dbeafe',
  borderRadius: 8,
  background: '#ffffff',
  marginBottom: 18,
}

const stripLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  textTransform: 'uppercase',
  fontWeight: 700,
}

const stripValueStyle: React.CSSProperties = {
  marginTop: 3,
  fontSize: 18,
  color: '#111827',
  fontWeight: 800,
}

const stripTrackStyle: React.CSSProperties = {
  flex: 1,
  height: 10,
  borderRadius: 999,
  background: '#e5e7eb',
  overflow: 'hidden',
}

const stripFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: 999,
  transition: 'width 0.2s ease',
}

const dayStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
}

const daySectionStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
  overflow: 'hidden',
}

const dayHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 18px',
  borderBottom: '1px solid #e2e8f0',
  background: '#f8fafc',
}

const dayTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  color: '#111827',
}

const dayMetaStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#64748b',
}

const dayTotalsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 20,
  fontWeight: 800,
  color: '#111827',
}

const memberListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

const memberBlockStyle: React.CSSProperties = {
  borderBottom: '1px solid #eef2f7',
}

const memberHeaderStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 64,
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 1fr) auto',
  alignItems: 'center',
  gap: 14,
  padding: '11px 18px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
}

const memberNameStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#111827',
  fontWeight: 800,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const memberRoleStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 11,
  color: '#94a3b8',
  textTransform: 'uppercase',
}

const memberMetricsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '128px 76px 76px 76px 92px 82px',
  alignItems: 'center',
  gap: 10,
}

const capacityBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  height: 34,
  borderRadius: 8,
  background: '#f8fafc',
  color: '#111827',
  fontWeight: 900,
  fontSize: 16,
}

const miniMetricStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#475569',
  fontWeight: 700,
  textAlign: 'right',
}

const memberProgressStyle: React.CSSProperties = {
  height: 4,
  background: '#eef2f7',
}

const memberProgressFillStyle: React.CSSProperties = {
  height: '100%',
  transition: 'width 0.2s ease',
}

const taskListStyle: React.CSSProperties = {
  background: '#f8fafc',
  padding: '8px 18px 14px 54px',
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
}

const taskRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 1fr) 92px 96px 80px 104px 22px',
  gap: 10,
  alignItems: 'center',
  width: '100%',
  border: '1px solid #e2e8f0',
  borderRadius: 7,
  background: '#ffffff',
  padding: '9px 11px',
  cursor: 'pointer',
  textAlign: 'left',
}

const taskMainWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  minWidth: 0,
}

const taskMainStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
}

const projectDotStyle = (color: string): React.CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: 999,
  background: color,
  flexShrink: 0,
})

const issueKeyStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#2563eb',
  fontWeight: 800,
  flexShrink: 0,
}

const taskTitleStyle: React.CSSProperties = {
  color: '#111827',
  fontSize: 13,
  fontWeight: 650,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const taskMetricStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#475569',
  fontWeight: 700,
  textAlign: 'right',
}

const logStatusBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 24,
  borderRadius: 7,
  padding: '0 8px',
  fontSize: 11,
  fontWeight: 900,
  textTransform: 'uppercase',
}

const taskMetaRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  flexWrap: 'wrap',
}

const taskStageMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#2563eb',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const taskLabelMetaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 20,
  borderRadius: 999,
  padding: '0 8px',
  fontSize: 11,
  fontWeight: 800,
}

const taskNotesStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  fontSize: 12,
  color: '#64748b',
  paddingTop: 3,
}

const taskTypeStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  fontSize: 11,
  color: '#2563eb',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  paddingTop: 2,
}

const noLogsStyle: React.CSSProperties = {
  border: '1px dashed #cbd5e1',
  borderRadius: 7,
  padding: '10px 12px',
  color: '#94a3b8',
  background: '#ffffff',
  fontSize: 13,
}

const emptyStyle: React.CSSProperties = {
  padding: 48,
  textAlign: 'center',
  color: '#94a3b8',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  background: '#ffffff',
}

const assignedPanelStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
  marginBottom: 18,
  overflow: 'hidden',
}

const assignedHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '15px 18px',
  borderBottom: '1px solid #e2e8f0',
  background: '#fbfdff',
}

const assignedTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 17,
  color: '#111827',
}

const assignedSubtitleStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#64748b',
}

const assignedCountStyle: React.CSSProperties = {
  minWidth: 38,
  height: 30,
  borderRadius: 8,
  background: '#eff6ff',
  color: '#1d4ed8',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 900,
}

const assignedListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

const assignedRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(260px, 1fr) minmax(150px, auto) 120px 110px 80px 22px',
  gap: 12,
  alignItems: 'center',
  width: '100%',
  minHeight: 48,
  padding: '10px 18px',
  border: 'none',
  borderBottom: '1px solid #eef2f7',
  background: '#ffffff',
  cursor: 'pointer',
  textAlign: 'left',
}

const assignedMainStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
}

const assignedMainWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  minWidth: 0,
}

const assignedTaskTitleStyle: React.CSSProperties = {
  color: '#111827',
  fontSize: 13,
  fontWeight: 750,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const assignedPersonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  minWidth: 0,
  color: '#475569',
  fontSize: 12,
  fontWeight: 700,
}

const assignedMetricStyle: React.CSSProperties = {
  color: '#475569',
  fontSize: 12,
  fontWeight: 800,
  textAlign: 'right',
}

const statusBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 24,
  borderRadius: 7,
  padding: '0 8px',
  fontSize: 11,
  fontWeight: 900,
  textTransform: 'uppercase',
}

const priorityTextStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'capitalize',
}

const assignedEmptyStyle: React.CSSProperties = {
  padding: '22px 18px',
  color: '#94a3b8',
  fontSize: 13,
}
